import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import {useEffect, useMemo, useRef, useState} from "react";
import {toast} from "react-hot-toast";
import {useDispatch} from "react-redux";
import {v4 as uuidv4} from "uuid";
import {axios_tenant} from "../../../axios";
import AIA81Modal from "./components/AIA81Modal";
import AIA83Modal from "./components/AIA83Modal";
import AssistantSidebar from "./components/AssistantSidebar";
import Chats from "./components/Chats";
import RCESB30Modal from "./components/RCESB30Modal";
import UsFilingModal from "./components/UsFilingModal";
import CreateCustomFormModal from "./components/CreateCustomFormModal";
import GenerateCustomFormModal from "./components/GenerateCustomFormModal";
import UsptoFeeCalculatorModal from "./components/UsptoFeeCalculatorModal";
import {formatInput, simplifyApplicationNumbers, validateSb08ReferenceCount, getCurrentTime} from "./utils/assistantUtils";

const charactersLimit = process.env.REACT_APP_JUNE_CHARACTER_LIMIT;
const INPUTS_TO_SIMPLIFY = ["patent_numbers", "application_nums_australia"];
// Slots for which the file upload (FileDrop) is enabled
const FILE_DROP_SLOTS = ["files", "application_nums_with_file", "file"];

function extractIntent(str) {
    const prefix = "Would you like to continue";

    // Check if the string starts with the prefix
    if (str.startsWith(prefix)) {
        // Use regex to extract the intent name (text after "with")
        const match = str.match(/with\s+(.+?)\s*\?$/i);
        if (match) {
            return match[1].trim(); // Return the extracted intent name
        }
    }

    return null; // Return null if it doesn't match the pattern
}
const TrexoChat = () => {
    // Manages the state
    const [switchedComponent, setSwitchedComponent] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [chatStarted, setChatStarted] = useState(false);
    const [messages, setMessages] = useState([]);
    const [chatInput, setChatInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [intentLoading, setIntentLoading] = useState(false);
    const [chatSessionId, setChatSessionId] = useState("");
    const [intentList, setIntentList] = useState([]);
    const [disabledFields, setDisabledFields] = useState({
        fileDrop: true,
        chatInput: false,
        dropdown: false,
        intentButton: true
    });
    const clickedSubmitRef = useRef(false);
    const autoFocusRef = useRef(null);
    // Count of references entered in the current SB-08 flow, validated against
    // the format-specific limit once the DocType (PDF/DOCX) is selected.
    const sb08ReferenceCountRef = useRef(0);
    const [slotName, setSlotName] = useState("");
    const [intentName, setIntentName] = useState("");
    const scrollToLastItem = useRef(null);
    const [assistantSidebarOpen, setAssistantSidebarOpen] = useState(false);
    const [showCancelButton, setShowCancelButton] = useState(false);
    const [filterData, setFilterData] = useState("");
    const [originatingRequestId, setOriginatingRequestId] = useState(null);
    const [requestId, setRequestId] = useState(null);
    const [dialogActionType, setDialogActionType] = useState("");

    const [usFilingIntent, setUsFilingIntent] = useState(false);
    const [feeCalcIntent, setFeeCalcIntent] = useState(false);
    const [aia81Intent, setAia81Intent] = useState(false);
    const [aia81ApplicationNums, setAia81ApplicationNums] = useState("");
    const [aia83Intent, setAia83Intent] = useState(false);
    const [rceSB30Intent, setRceSb30Intent] = useState(false);
    const [aia83ApplicationNums, setAia83ApplicationNums] = useState("");
    const [rceSB30ApplicationNums, setrceSB30ApplicationNums] = useState("");
    const [createCustomFormIntent, setCreateCustomFormIntent] = useState(false);
    const [generateCustomFormIntent, setGenerateCustomFormIntent] = useState(false);

    // ShellOAIntent states - Enhanced state management
    const [shellOAIntent, setShellOAIntent] = useState(false);
    const [shellOAFillStatus, setShellOAFillStatus] = useState("pending");
    const [shellOAApplicationNums, setShellOAApplicationNums] = useState("");
    const [shellOAPopulatedKeys, setShellOAPopulatedKeys] = useState(null);
    const [executeApiCall, setExecuteApiCall] = useState(false);
    const [pendingLexResponse, setPendingLexResponse] = useState(null);
    const [apiProcessingComplete, setApiProcessingComplete] = useState(false);

    let dispatch = useDispatch();

        // Only admins may create/edit custom forms; everyone can generate from existing ones.
    const isAdmin = localStorage.getItem("TENANT_ROLE") === "admin";

    // Hide the "Create New Custom Form" option from non-admin users while keeping
    // "Generate Custom Form" (Existing) available to everyone.
    const filteredIntentList = useMemo(() => {
        if (isAdmin || !intentList?.["Custom Forms"]) return intentList;
        return {
            ...intentList,
            "Custom Forms": intentList["Custom Forms"].filter(
                (intent) => intent.description !== "Create New Custom Form"
            )
        };
    }, [intentList, isAdmin]);

    // Sets the chat session id
    useEffect(() => {
        setChatSessionId(uuidv4());
    }, []);

    // Scrolls to the last message
    useEffect(() => {
        setTimeout(() => {
            scrollToLastItem.current?.lastElementChild?.scrollIntoView({
                behavior: "smooth"
            });
        }, 1);
    }, [messages, switchedComponent]);
    // Fetches the intents
    useEffect(() => {
        const fetchIntents = async () => {
             setIntentLoading(true); 
            try {
                const { data } = await axios_tenant.get("/trexo-assistant/getIntentList");
                setIntentList(data);
                if (data) {
                    dispatch({
                        type: "JUNE_INTENT_LIST",
                        payload: data,
                    });
                }
            } catch (error) {
            }
            finally {
            setIntentLoading(false); 
            }
        };
        fetchIntents();
    }, [dispatch]);

    // Enhanced effect for ShellOAIntent API handling
    useEffect(() => {
        const processShellOAIntent = async () => {
            if (executeApiCall && shellOAIntent && shellOAApplicationNums) {
                await downloadAndValidateDocuments(shellOAApplicationNums, chatSessionId);
            }
        };
        processShellOAIntent();
    }, [executeApiCall, shellOAIntent, shellOAApplicationNums, chatSessionId]);

    // New effect to handle post-API processing for ShellOAIntent
    useEffect(() => {
        const handlePostApiProcessing = async () => {
            if (apiProcessingComplete && pendingLexResponse && shellOAIntent) {
                setLoading(true);
                await processLexResponseWithShellOA(pendingLexResponse);
                setPendingLexResponse(null);
                setApiProcessingComplete(false);
            }
        };
        handlePostApiProcessing();
    }, [apiProcessingComplete, pendingLexResponse, shellOAIntent]);

    /**
     * Enhanced chat submit handler with ShellOAIntent preprocessing.
     * Validates input, performs file size check (max 25 MB) for document generation intents,
     * and submits message to standard or special handlers.
     *
     * @param {Event} e - The event object.
     * @param {string} [submittedInput=chatInput] - The user input text.
     */
    const handleChatSubmit = async (e, submittedInput = chatInput) => {
        e.preventDefault();
        const trimmedInput = submittedInput.trim();

        if (loading) return;

        if (!trimmedInput && uploadedFiles.length === 0) {
            toast.error("No input detected. Please enter the required information and try again.");
            return;
        }

        // Special handling for ShellOAIntent application_nums
        if (slotName === "application_nums" && intentName.toLowerCase() === "shelloaintent") {
            const simplifiedAppNums = simplifyApplicationNumbers(trimmedInput, intentName);
            const firstAppNum = simplifiedAppNums.split(/[\s,;]+/)[0];
            setLoading(true);
            setShellOAIntent(true);
            setShellOAApplicationNums(firstAppNum);
            setExecuteApiCall(true);
            setShellOAFillStatus("pending");

            // Add user message immediately
            // setMessages((prev) => [...prev, { from: "user", content: simplifiedAppNums }]);
            setChatInput("");
            setUploadedFiles([]);

            // Continue with normal Lex submission but store response for later processing
            let inputText = simplifiedAppNums;

            await handleModuleSubmitWithShellOA({ textInput: inputText });

            return;
        }

        // Handle other special intents
        if (slotName === "formDetails" && intentName.toLowerCase() === "aia81adocgenintent") {
            const inputText = simplifyApplicationNumbers(trimmedInput, intentName);
            if (inputText) {
                const time = getCurrentTime();
                setMessages((prev) => [...prev, { from: "user", content: inputText, time }]);
                setAia81ApplicationNums(inputText);
                setAia81Intent(true);
            }
            return;
        }

        if (slotName === "formDetails" && intentName.toLowerCase() === "aia83docgenintent") {
            const inputText = simplifyApplicationNumbers(trimmedInput, intentName);
            if (inputText) {
                const time = getCurrentTime();
                setMessages((prev) => [...prev, { from: "user", content: inputText, time }]);
                setAia83ApplicationNums(inputText);
                setAia83Intent(true);
            }
            return;
        }

        if (slotName === "formDetails" && intentName.toLowerCase() === "sb30efsdocgenintent") {
            const inputText = simplifyApplicationNumbers(trimmedInput, intentName);
            if (inputText) {
                const time = getCurrentTime();
                setMessages((prev) => [...prev, { from: "user", content: inputText, time }]);
                setrceSB30ApplicationNums(inputText);
                setRceSb30Intent(true);
            }
            return;
        }

        // Standard processing for other intents.
        // Custom Forms (create/generate) go through simplifyApplicationNumbers
        // too, same as every other intent - it's EP/Global-aware for those two
        // intent names specifically (see CUSTOM_FORM_INTENTS in assistantUtils.js),
        // so publication numbers carrying a country/kind code (e.g. EP1234567A1)
        // keep their shape instead of being stripped to bare digits; validation
        // still happens server-side, against the saved template's database (see
        // customforms_handler.py).
        let inputText = slotName === "application_nums" || slotName === "application_nums_with_file"
            ? simplifyApplicationNumbers(trimmedInput, intentName)
            : INPUTS_TO_SIMPLIFY.includes(slotName)
                ? formatInput(trimmedInput, intentName)
                : trimmedInput.replace(/</g, "&lt;").replace(/>/g, "&gt;");

        // Capture the SB-08 reference count so it can be validated against the
        // format-specific limit when the DocType (PDF/DOCX) is later selected.
        if (
            intentName?.toLowerCase() === "sb08intent" &&
            INPUTS_TO_SIMPLIFY.includes(slotName) &&
            inputText
        ) {
            sb08ReferenceCountRef.current = inputText.split(",").filter(Boolean).length;
        }

        if (slotName === "files" || slotName === "file") {
            if (slotName === "files" && (intentName.toLowerCase() === "37cfrdocgenintent" || intentName.toLowerCase() === "poadocgenintent")) {
                const hasLargeFile = uploadedFiles.some((file) => file.size > 25 * 1024 * 1024);
                if (hasLargeFile) {
                    toast.error("File size cannot exceed 25 MB");
                    return;
                }
            }
            inputText = `${uploadedFiles.length} ${uploadedFiles.length > 1 ? "files" : "file"} attached<br/>${uploadedFiles
                .map((file) => file.name)
                .join("<br/>")}`;
        }

        let largeSlotData;
        if (inputText?.length > charactersLimit) {
            largeSlotData = inputText;
        }

        if ((inputText || uploadedFiles.length > 0) && !largeSlotData) {
            await handleModuleSubmit({ textInput: inputText });
        } else if (largeSlotData) {
            await handleModuleSubmit({ textInput: inputText, largeSlotData, originatingRequestId, requestId });
        }
    };

    // Enhanced module submit handler for ShellOAIntent
    const handleModuleSubmitWithShellOA = async ({ textInput, largeSlotData, originatingRequestId, requestId, usFilingFullData }) => {
        if (chatStarted === false) {
            setChatStarted(true);
        }

        if (textInput) {
            const time = getCurrentTime();
            setMessages((prev) => [...prev, {from: "user", content: textInput, time }]);
        }

        setLoading(true);

        // Prepare the form data
        let formData = new FormData();
        if (clickedSubmitRef.current) {
            uploadedFiles.forEach((file) => formData.append("files", file));
        }

        if (usFilingFullData?.uploadedFiles.length > 0) {
            usFilingFullData?.uploadedFiles.forEach((file) => formData.append("files", file));
        }

        formData.append("sessionId", chatSessionId);

        if (largeSlotData) {
            formData.append("textInput", "Sample Text");
            formData.append("largeSlotData", largeSlotData);
            formData.append("originatingRequestId", originatingRequestId);
            formData.append("requestId", requestId);
            formData.append("slotName", slotName);
        } else {
            if (slotName === "npl_references") {
                formData.append("textInput", textInput.replace(/\r?\n/g, "\\n"));
            } else if (intentName.toLowerCase() === "appgen") {
                const usFilingTextInput = usFilingFullData.usFilingTextData;
                formData.append("textInput", usFilingTextInput);
                formData.append("isUsFiling", true);
            } else {
                formData.append("textInput", textInput);
            }
        }

        formData.append("slotName", slotName);
        formData.append("intentName", intentName);

        try {
            
            const response = await axios_tenant.post("/trexo-assistant/getChatResponse", formData, {
                headers: { sessionId: chatSessionId }
            });

            if (response.status === 200) {
                // Store the response for later processing after API call completes
                setPendingLexResponse(response);
            }
        } catch (error) {
            const errorUrl = error?.response?.config?.url;
            
            const isTrexoChat = errorUrl?.includes("/trexo-assistant/getChatResponse");
            if (isTrexoChat && error?.response?.status === 400) {
                const time = getCurrentTime();
                setMessages((prev) => [...prev, { from: "assistant", content: error?.response?.data?.message, time }]);
            }
            handleStop();
        } finally {
            setUploadedFiles([]);

            clickedSubmitRef.current = false;
        }
    };

    // Process Lex response with ShellOA conditional logic
    const processLexResponseWithShellOA = async (response) => {
        // setLoading(true);
        const responseMessages = response.data.message;
        const { name, state } = response.data?.sessionState?.intent ?? {};
        setIntentName(name);
        const lastAssistantMessage = [...responseMessages].reverse().find((m) => m.from === "assistant");
        const buttons = Array.isArray(lastAssistantMessage?.buttons) ? lastAssistantMessage.buttons : [];

        const isConfirmationButtons =
            buttons.length === 2 &&
            buttons.every(
                (b) =>
                    typeof b?.value === "string" &&
                    ["yes", "no"].includes(b.value.toLowerCase())
            );

        const awaitingSelection = buttons.length > 0 && !isConfirmationButtons;
        const { slotToElicit: slotNameCheck, type: dialogAction } = response.data.sessionState?.dialogAction || {};

        const originatingRequestId = response?.data?.sessionState?.originatingRequestId;
        const requestId = response?.data?.sessionState?.sessionAttributes?.requestId;

        originatingRequestId && setOriginatingRequestId(originatingRequestId);
        requestId && setRequestId(requestId);

        // ShellOAIntent conditional handling for "files" slot
        if (name?.toLowerCase() === "shelloaintent" && slotNameCheck === "files") {
            if (shellOAFillStatus === "success" && shellOAPopulatedKeys) {
                // Success path: Auto-populate files slot and skip to next slot
                const successMessage = {
                    from: "assistant",
                    content: "Files are successfully retrieved for the provided application number.",
                    time: getCurrentTime()
                };
                setMessages(prev => [...prev, successMessage]);

                // Auto-submit the populated keys to proceed to next slot
                await autoSubmitPopulatedFiles("files");
                setLoading(false);
                return; // Skip normal message processing
            } else if (shellOAFillStatus === "failure") {
                // Failure path: Show standard files elicitation
                const failureMessage = {
                    from: "assistant",
                    content: "We couldn’t find any Office Action documents for this application number. Please upload or drag and drop the Rejection PDF and Rejection DOCX files.",
                    time: getCurrentTime()

                };
                setMessages(prev => [...prev, failureMessage]);

                setSlotName("files");
                setDisabledFields({
                    fileDrop: false,
                    chatInput: true,
                    dropdown: true,
                    intentButton: true
                });
                setLoading(false);
                return;
            }
        }

        // Standard message processing for non-ShellOA or non-files slots
        if (responseMessages.length > 0) {
            responseMessages.forEach(msg => {
                if (msg.content && msg.content.length > 0) {
                    const time = getCurrentTime();
                    setMessages(prev => [...prev, {...msg,snapshotIntentState: state, time }]);
                } else {
                    const time = getCurrentTime();
                    setMessages(prev => [
                        ...prev,
                        {
                            from: "assistant",
                            content: `Your request ID: ${requestId}, is being processed and will be delivered to your mailbox upon completion. You can also track its progress via "Track Task Here".`,
                            time
                        }
                    ]);
                }
            });
            setLoading(false);
        }

        // Handle other intent-specific logic
        if (name?.toLowerCase() === "appgen" && dialogAction?.toLowerCase() !== "close") {
            setUsFilingIntent(true);
        } else if (name?.toLowerCase() === "appgen" && dialogAction?.toLowerCase() === "close") {
            setUsFilingIntent(false);
            setIntentName("");
        }

        if (name?.toLowerCase() === "usptofeecalculatorintent" && dialogAction?.toLowerCase() !== "close") {
            setFeeCalcIntent(true);
        } else if (name?.toLowerCase() === "usptofeecalculatorintent" && dialogAction?.toLowerCase() === "close") {
            setFeeCalcIntent(false);
            setIntentName("");
        }

        if (!slotNameCheck && dialogAction?.toLowerCase() === "close") {
            setChatSessionId(uuidv4());
        }

        setDialogActionType(dialogAction);
        setDisabledFields((prevDisabledFields) => ({
            ...prevDisabledFields,
            dropdown: (awaitingSelection || dialogAction !== "Close"),
            fileDrop: !FILE_DROP_SLOTS.includes(slotNameCheck),
            chatInput: slotNameCheck === "files"
        }));

        setSlotName(slotNameCheck || "");
        autoFocusRef.current?.focus();
    };

    // Auto-submit populated files for ShellOAIntent
    const autoSubmitPopulatedFiles = async (slotName) => {
        // setLoading(true);

        let formData = new FormData();
        formData.append("sessionId", chatSessionId);
        formData.append("slotName", slotName);
        formData.append("intentName", intentName);

        // Send the populated keys as the text input
        let populatedFilesData = JSON.stringify([
            shellOAPopulatedKeys.pdfKey,shellOAPopulatedKeys.docxKey
        ]);
        if(slotName === "file"){
            populatedFilesData = JSON.stringify([shellOAPopulatedKeys.claimDocxKey]);
        }
        formData.append("textInput", populatedFilesData);

        try {
           
            const response = await axios_tenant.post("/trexo-assistant/getChatResponse", formData, {
                headers: { sessionId: chatSessionId }
            });

            if (response.status === 200) {
                // Process the response normally
                await processStandardLexResponse(response);
            }
        } catch (error) {
            // console.error("Error auto-submitting populated files:", error);
            handleStop();
        } finally {

        }
    };

    // Standard Lex response processing (unchanged from original)
    const processStandardLexResponse = async (response) => {
        const responseMessages = response.data.message;
        const { name, state } = response.data?.sessionState?.intent ?? {};
        setIntentName(name);
        const lastAssistantMessage = [...responseMessages].reverse().find((m) => m.from === "assistant");
        const buttons = Array.isArray(lastAssistantMessage?.buttons) ? lastAssistantMessage.buttons : [];

        const isConfirmationButtons =
            buttons.length === 2 &&
            buttons.every(
                (b) =>
                    typeof b?.value === "string" &&
                    ["yes", "no"].includes(b.value.toLowerCase())
            );

        const awaitingSelection = buttons.length > 0 && !isConfirmationButtons;
        const { slotToElicit: slotNameCheck, type: dialogAction } = response.data.sessionState?.dialogAction || {};
        if (name?.toLowerCase() === "shelloaintent" && slotNameCheck === "file" && shellOAPopulatedKeys?.claimDocxKey) {
            await autoSubmitPopulatedFiles("file");
            return;
        }

           if (name?.toLowerCase() === "createcustomformintent" && slotNameCheck === "form_input") {
            if (!isAdmin) {
                setMessages((prev) => [...prev, {
                    from: "assistant",
                    content: "You don't have permission to create custom forms. Please contact your admin."
                }]);
                setLoading(false);
                handleStop();
                return;
            }

            const originatingRequestId = response?.data?.sessionState?.originatingRequestId;
            const requestId = response?.data?.sessionState?.sessionAttributes?.requestId;
            originatingRequestId && setOriginatingRequestId(originatingRequestId);
            requestId && setRequestId(requestId);
            setSlotName("form_input");
            setCreateCustomFormIntent(true);
            setLoading(false);
            return;
        }

        if (name?.toLowerCase() === "generatecustomformintent" && slotNameCheck === "form_id") {

            const originatingRequestId = response?.data?.sessionState?.originatingRequestId;
            const requestId = response?.data?.sessionState?.sessionAttributes?.requestId;
            originatingRequestId && setOriginatingRequestId(originatingRequestId);
            requestId && setRequestId(requestId);
            setSlotName("form_id");
            setGenerateCustomFormIntent(true);
            setLoading(false);
            return;
        }

        if (responseMessages.length > 0) {
            responseMessages.forEach(msg => {
                if (msg.content && msg.content.length > 0) {
                const time = getCurrentTime();
                setMessages(prev => [...prev, {
                    ...msg,
                    snapshotIntentState: state,
                    time
                    
                }]);
                } else {
                    const time = getCurrentTime();
                    setMessages(prev => [
                        ...prev,
                        {
                            from: "assistant",
                            content: `Your request ID: ${response.data.sessionState?.sessionAttributes?.requestId}, is being processed and will be delivered to your mailbox upon completion. You can also track its progress via "Track Task Here".`,
                            time
                        }
                    ]);
                }
            });

            const originatingRequestId = response?.data?.sessionState?.originatingRequestId;
            const requestId = response?.data?.sessionState?.sessionAttributes?.requestId;

            originatingRequestId && setOriginatingRequestId(originatingRequestId);
            requestId && setRequestId(requestId);

            if (name?.toLowerCase() === "appgen" && dialogAction?.toLowerCase() !== "close") {
                setUsFilingIntent(true);
            } else if (name?.toLowerCase() === "appgen" && dialogAction?.toLowerCase() === "close") {
                setUsFilingIntent(false);
                setIntentName("");
            }

            if (name?.toLowerCase() === "usptofeecalculatorintent" && dialogAction?.toLowerCase() !== "close") {
                setFeeCalcIntent(true);
            } else if (name?.toLowerCase() === "usptofeecalculatorintent" && dialogAction?.toLowerCase() === "close") {
                setFeeCalcIntent(false);
                setIntentName("");
            }

            if (!slotNameCheck && dialogAction?.toLowerCase() === "close") {
                setChatSessionId(uuidv4());
            }

            setDialogActionType(dialogAction);
            setDisabledFields((prevDisabledFields) => ({
                ...prevDisabledFields,
                dropdown: (awaitingSelection || dialogAction !== "Close"),
                fileDrop: !FILE_DROP_SLOTS.includes(slotNameCheck),
                chatInput: slotNameCheck === "files"
            }));

            setSlotName(slotNameCheck || "");
        }
    };

    // Standard module submit (for non-ShellOA intents)
    // displayText: optional label shown in the chat bubble instead of textInput (which is what goes to the backend).
    const handleModuleSubmit = async ({ textInput, displayText, largeSlotData, originatingRequestId, requestId, usFilingFullData }) => {
        // SB-08: enforce the format-specific reference count limit when the
        // output format (DocType slot) is chosen, before sending it to Lex.
        if (
            slotName === "DocType" &&
            intentName?.toLowerCase() === "sb08intent" &&
            !validateSb08ReferenceCount(sb08ReferenceCountRef.current, textInput)
        ) {
            return;
        }

        let continueIntent = null
        if (chatStarted === false) {
            setChatStarted(true);
        }

        if (textInput) {
            const msgIntent = messages[messages.length - 1] ? extractIntent(messages[messages.length - 1].content) : null;
            if (!msgIntent) {
                const time = getCurrentTime();
                setMessages((prev) => [...prev, { from: "user", content: displayText ?? textInput, time }]);
            }
            if (msgIntent) {
                if (textInput.toLowerCase() === "yes") {
                    const time = getCurrentTime();
                    setMessages((prev) => [...prev, { from: "user", content: displayText ?? textInput, time }]);
                    continueIntent = msgIntent;
                } else if (textInput.toLowerCase() === "no") {
                    const time = getCurrentTime();
                    setMessages((prev) => [
                        ...prev,
                        { from: "user", content: displayText ?? textInput, time },
                        {
                            content: "Enter your query or select a task to get started",
                            from: "frontend",
                            time
                        }]);
                    setChatInput("");
                    clickedSubmitRef.current = false;
                    autoFocusRef.current?.focus();
                    return  // reset incase User does not want to continue with the same intent
                }
                else {
                    const time = getCurrentTime();
                    setMessages((prev) => [...prev, { from: "user", content: displayText ?? textInput, time }]);
                }
            }
        }

        setLoading(true);

        // Prepare the form data
        let formData = new FormData();
        if (clickedSubmitRef.current) {
            uploadedFiles.forEach((file) => formData.append("files", file));
        }

        if (usFilingFullData?.uploadedFiles.length > 0) {
            usFilingFullData?.uploadedFiles.forEach((file) => formData.append("files", file));
        }

        formData.append("sessionId", chatSessionId);
        if (largeSlotData) {
            formData.append("textInput", "Sample Text");
            formData.append("largeSlotData", largeSlotData);
            formData.append("originatingRequestId", originatingRequestId);
            formData.append("requestId", requestId);
            formData.append("slotName", slotName);
           if (intentName.toLowerCase() === "appgen" || intentName.toLowerCase() === "createcustomformintent") formData.append("isUsFiling", true);
        } else {
            if (slotName === "npl_references") {
                formData.append("textInput", textInput.replace(/\r?\n/g, "\\n"));
            } else {
                formData.append("textInput", textInput);
            }
        }
        if (continueIntent) {
            formData.set("textInput", continueIntent);
        }
        // intentName is only for pre processing, textInput is what goes to LEX for intent trigger.
        formData.append("intentName", intentName);

        setUploadedFiles([]);
        setChatInput("");
       
        try {
            
            const response = await axios_tenant.post("/trexo-assistant/getChatResponse", formData, {
                headers: { sessionId: chatSessionId }
            });

            if (response.status === 200) {
                await processStandardLexResponse(response);
            }
        } catch (error) {
            
            const errorUrl = error?.response?.config?.url;
            const isTrexoChat = errorUrl?.includes("/trexo-assistant/getChatResponse");
            if (isTrexoChat && error?.response?.status === 400) {
                const time = getCurrentTime();
                setMessages((prev) => [...prev, { from: "assistant", content: error?.response?.data?.message, time }]);
            }
            handleStop();
        } finally {
            setUploadedFiles([]);
            setLoading(false);
            setChatInput("");
            clickedSubmitRef.current = false;
            autoFocusRef.current?.focus();
        }
    };

    const handleStop = () => {
        setDisabledFields({
            dropdown: false,
            intentButton: true,
            fileDrop: true,
            chatInput: false
        });
        setChatSessionId(uuidv4());
        setChatInput("");
        setUploadedFiles([]);
        setSlotName("");
        setIntentName("");
        setShowCancelButton(false);
        setDialogActionType("");
        sb08ReferenceCountRef.current = 0;
        setUsFilingIntent(false);
        setFeeCalcIntent(false);
        setAia81Intent(false);
        setAia81ApplicationNums("");
        setAia83Intent(false);
        setRceSb30Intent(false);
        setrceSB30ApplicationNums("");
        setAia83ApplicationNums("");

        // Reset ShellOAIntent states
        setShellOAIntent(false);
        setShellOAFillStatus("pending");
        setShellOAApplicationNums("");
        setShellOAPopulatedKeys(null);
        setExecuteApiCall(false);
        setPendingLexResponse(null);
        setApiProcessingComplete(false);
    };

    // Enhanced document download and validation
    const downloadAndValidateDocuments = async (applicationNums, sessionId) => {
        setLoading(true);
        try {
            // console.log(`Downloading documents for application: ${applicationNums}`);

            const response = await axios_tenant.get("/trexo-assistant/oa/downloadDocuments", {
                params: { applicationNumber: applicationNums },
                headers: {
                    sessionId: sessionId,
                }
            });

            if (response.status < 400 && response.data?.fileKeys) {
                // console.log("Document download successful:", response.data.fileKeys);
                setShellOAPopulatedKeys({
                    pdfKey: response.data.fileKeys.pdf,
                    docxKey: response.data.fileKeys.docx,
                    claimDocxKey: response.data.fileKeys.claimsDocx
                });
                setShellOAFillStatus("success");
                // toast.success("Documents downloaded and validated successfully.");
            } else {
                // console.log("Document download failed or no file keys returned");
                setShellOAFillStatus("failure");
                // toast.error("Unable to automatically retrieve files. Please upload them manually.");
            }
        } catch (error) {
            // console.error("Error downloading/validating documents:", error);
            setShellOAFillStatus("failure");
            // toast.error("Failed to process application numbers. Please provide files manually.");
        } finally {
            setExecuteApiCall(false);
            setApiProcessingComplete(true);

        }
    };

    return (
        <Box
            sx={{
                height: "calc(100vh - 144px)",
                backgroundColor: "#FEFEFE",
                display: "flex",
                flexDirection: "row",
                position: "relative",
                overflow: "hidden",
                width: "100%"
            }}
        >
            {/* Configuration Sidebar */}
            <AssistantSidebar
                handleChatSubmit={handleChatSubmit}
                disabledFields={disabledFields}
                handleModuleSubmit={handleModuleSubmit}
                intentList={filteredIntentList}
                loading={intentLoading || loading}
                setAssistantSidebarOpen={setAssistantSidebarOpen}
                assistantSidebarOpen={assistantSidebarOpen}
                setUploadedFiles={setUploadedFiles}
                uploadedFiles={uploadedFiles}
                setSwitchedComponent={setSwitchedComponent}
                clickedSubmit={clickedSubmitRef}
                intentName={intentName}
            />

            {/* Chat Area */}
            <Chats
                uploadedFiles={uploadedFiles}
                intentName={intentName}
                setUploadedFiles={setUploadedFiles}
                chatInput={chatInput}
                charactersLimit={charactersLimit}
                chatStarted={chatStarted}
                disabledFields={disabledFields}
                handleModuleSubmit={handleModuleSubmit}
                showCancelButton={showCancelButton}
                loading={loading}
                setLoading={setLoading}
                setAssistantSidebarOpen={setAssistantSidebarOpen}
                messages={messages}
                setMessages={setMessages}
                setChatStarted={setChatStarted}
                scrollToLastItem={scrollToLastItem}
                setChatInput={setChatInput}
                setSwitchedComponent={setSwitchedComponent}
                switchedComponent={switchedComponent}
                handleChatSubmit={handleChatSubmit}
                intentList={intentList}
                chatSessionId={chatSessionId}
                autoFocusRef={autoFocusRef}
                handleStop={handleStop}
                setFilterData={setFilterData}
                clickedSubmit={clickedSubmitRef}
                filterData={filterData}
                dialogActionType={dialogActionType}
                slotName={slotName}
            />

            {/* US filing modal */}
            <Modal open={!!usFilingIntent} sx={{
                width: "85%",
                opacity: 1,
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}>
                <UsFilingModal
                    setUsFilingIntent={setUsFilingIntent}
                    handleModuleSubmit={handleModuleSubmit}
                    handleStop={handleStop}
                    setMessages={setMessages}
                    originatingRequestId={originatingRequestId}
                    requestId={requestId}
                />
            </Modal>

            <Modal open={!!feeCalcIntent} sx={{
                width: "85%",
                opacity: 1,
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}>
                <UsptoFeeCalculatorModal
                    setFeeCalcIntent={setFeeCalcIntent}
                    handleModuleSubmit={handleModuleSubmit}
                    handleStop={handleStop}
                    setMessages={setMessages}
                    originatingRequestId={originatingRequestId}
                    requestId={requestId}
                />
            </Modal>

            <Modal open={!!aia81Intent && aia81ApplicationNums} sx={{
                width: "85%",
                opacity: 1,
                mx: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}>
                <AIA81Modal
                    setAia81Intent={setAia81Intent}
                    originatingRequestId={originatingRequestId}
                    requestId={requestId}
                    handleModuleSubmit={handleModuleSubmit}
                    handleStop={handleStop}
                    setMessages={setMessages}
                    applicationNumbers={aia81ApplicationNums}
                />
            </Modal>

            <Modal
                open={!!rceSB30Intent && rceSB30ApplicationNums !== ""}
                disableEnforceFocus
                sx={{
                    width: "85%",
                    opacity: 1,
                    mx: "auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}>
                <RCESB30Modal
                    setRceSb30Intent={setRceSb30Intent}
                    originatingRequestId={originatingRequestId}
                    requestId={requestId}
                    handleModuleSubmit={handleModuleSubmit}
                    handleStop={handleStop}
                    setMessages={setMessages}
                    applicationNumbers={rceSB30ApplicationNums}
                />
            </Modal>

                        <Modal
                open={!!aia83Intent && aia83ApplicationNums !== ""}
                sx={{
                    width: "85%",
                    opacity: 1,
                    mx: "auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}>
                <AIA83Modal
                    setAia83Intent={setAia83Intent}
                    originatingRequestId={originatingRequestId}
                    requestId={requestId}
                    handleModuleSubmit={handleModuleSubmit}
                    handleStop={handleStop}
                    setMessages={setMessages}
                    applicationNumbers={aia83ApplicationNums}
                />
            </Modal>
             <CreateCustomFormModal
                open={createCustomFormIntent && isAdmin}
                onClose={() => {
                    setCreateCustomFormIntent(false);
                    handleStop();
                    setMessages((prev) => [...prev, { from: "assistant", content: "Request cancelled." }]);
                }}
                onSaved={() => {
                    setCreateCustomFormIntent(false);
                }}
                handleModuleSubmit={handleModuleSubmit}
                intentList={filteredIntentList}
                originatingRequestId={originatingRequestId}
                requestId={requestId}
            />

            <GenerateCustomFormModal
                open={generateCustomFormIntent}
                onClose={() => {
                    setGenerateCustomFormIntent(false);
                    handleStop();
                    setMessages((prev) => [...prev, { from: "assistant", content: "Request cancelled." }]);
                }}
                onSelected={() => {
                    setGenerateCustomFormIntent(false);
                }}
                handleModuleSubmit={handleModuleSubmit}
                isAdmin={isAdmin}
            />
        </Box>
    );
};

export default TrexoChat;